import React, { useMemo, useRef, useState } from "react";
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, Radio, RadioGroup, Step, StepLabel, Stepper,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  Autocomplete, InputAdornment, Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import {
  useUploadAppReleaseMutation, useInspectAppReleaseMutation,
  useGetDeployTargetsQuery, useGetDevicesQuery,
} from "../../Api/attoDeskApi";
import { IDevice } from "../../Api/Interface/api.interface";
import { useNotifier } from "../../Core/Notifier";
import { NAVY, GOLD, GOLD_SOFT, TEXT_SECONDARY, BORDER } from "../../theme/tokens";
import {
  formatBytes, DEPLOY_TARGETS, POLICIES, INSTALL_MODES, ROLLOUT_STAGES,
} from "./updateShared";

const STEPS = [
  "Release Information", "APK File", "Deployment Target",
  "Update Policy", "Installation Schedule", "Review & Upload",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}

const UploadWizard: React.FC<Props> = ({ open, onClose, onUploaded }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [upload, { isLoading: uploading }] = useUploadAppReleaseMutation();
  const [inspect] = useInspectAppReleaseMutation();
  const { data: targetsData } = useGetDeployTargetsQuery(undefined, { skip: !open });
  const { data: devicesData } = useGetDevicesQuery({ searchText: "" }, { skip: !open });

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    platform: "android",
    version_name: "",
    version_code: "",
    release_channel: "stable",
    release_notes: "",
    deploy_target: "none",
    policy: "optional",
    install_mode: "maintenance_window",
    scheduled_at: "",
    rollout_stage: "100",
    failure_threshold_percent: "" as string,
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [detected, setDetected] = useState<any>(null);

  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [deviceIds, setDeviceIds] = useState<number[]>([]);
  const [confirmMandatory, setConfirmMandatory] = useState(false);

  // Device-table filters (Selected devices).
  const [dfSearch, setDfSearch] = useState("");
  const [dfCompany, setDfCompany] = useState<number | "">("");
  const [dfStatus, setDfStatus] = useState<string>("");
  const [dfVersion, setDfVersion] = useState<string>("");
  const [dfGroup, setDfGroup] = useState<number | "">("");

  const companies: Array<{ id: number; name: string; active_devices: number }> =
    (targetsData?.data as any)?.companies ?? [];
  const groups: Array<{ id: number; name: string; device_count: number }> =
    (targetsData?.data as any)?.groups ?? [];
  const allDevices: IDevice[] = Array.isArray(devicesData?.data)
    ? ((devicesData?.data as unknown) as IDevice[]) : [];

  const filteredDevices = useMemo(() => allDevices.filter((d) => {
    if (dfSearch && !(`${d.device_code} ${d.device_name} ${d.location ?? ""}`
      .toLowerCase().includes(dfSearch.toLowerCase()))) return false;
    if (dfCompany && d.company_id !== dfCompany) return false;
    if (dfStatus && d.status !== dfStatus) return false;
    if (dfVersion && (d.app_version ?? "") !== dfVersion) return false;
    if (dfGroup && d.playlist_group_id !== dfGroup) return false;
    return true;
  }), [allDevices, dfSearch, dfCompany, dfStatus, dfVersion, dfGroup]);

  const versionOptions = useMemo(
    () => Array.from(new Set(allDevices.map((d) => d.app_version).filter(Boolean))) as string[],
    [allDevices],
  );

  const reset = () => {
    setStep(0); setFile(null); setDetected(null);
    setCompanyIds([]); setGroupIds([]); setDeviceIds([]); setConfirmMandatory(false);
    setForm({
      platform: "android", version_name: "", version_code: "", release_channel: "stable",
      release_notes: "", deploy_target: "none", policy: "optional",
      install_mode: "maintenance_window", scheduled_at: "", rollout_stage: "100",
      failure_threshold_percent: "",
    });
  };

  const onPickFile = async (f: File | null) => {
    setFile(f); setDetected(null);
    if (!f) return;
    const fd = new FormData();
    fd.append("apk", f);
    try {
      const res: any = await inspect(fd).unwrap();
      if (res?.status) {
        setDetected(res.data);
        // Auto-fill version fields from the APK if still empty.
        set({
          version_name: form.version_name || res.data.version_name || "",
          version_code: form.version_code || (res.data.version_code ? String(res.data.version_code) : ""),
        });
      }
    } catch { /* inspection is best-effort; ignore failures */ }
  };

  // Count of devices this deployment will target (for the review summary).
  const targetCount = useMemo(() => {
    switch (form.deploy_target) {
      case "all_active": return allDevices.filter((d) => d.status !== "disabled").length;
      case "companies": return companies.filter((c) => companyIds.includes(c.id))
        .reduce((s, c) => s + c.active_devices, 0);
      case "groups": return groups.filter((g) => groupIds.includes(g.id))
        .reduce((s, g) => s + g.device_count, 0);
      case "devices": return deviceIds.length;
      default: return 0;
    }
  }, [form.deploy_target, allDevices, companies, groups, companyIds, groupIds, deviceIds]);

  const needsMandatoryConfirm =
    form.deploy_target !== "none" && form.policy === "mandatory" &&
    (form.deploy_target === "all_active" || targetCount > 0);

  const canNext = (): boolean => {
    if (step === 0) return !!form.version_name.trim() && !!form.version_code.trim();
    if (step === 1) return form.platform !== "android" || !!file;
    if (step === 2) {
      if (form.deploy_target === "companies") return companyIds.length > 0;
      if (form.deploy_target === "groups") return groupIds.length > 0;
      if (form.deploy_target === "devices") return deviceIds.length > 0;
      return true;
    }
    return true;
  };

  const doUpload = async () => {
    if (needsMandatoryConfirm && !confirmMandatory) {
      showErrorMessage("Please confirm the mandatory deployment");
      return;
    }
    const fd = new FormData();
    fd.append("platform", form.platform);
    fd.append("version_code", form.version_code);
    fd.append("version_name", form.version_name);
    fd.append("release_channel", form.release_channel);
    if (form.release_notes) fd.append("release_notes", form.release_notes);
    // Mandatory policy also sets the legacy force_update flag.
    if (form.policy === "mandatory") fd.append("force_update", "1");
    if (file) fd.append("apk", file);

    fd.append("deploy_target", form.deploy_target);
    if (form.deploy_target !== "none") {
      fd.append("policy", form.policy);
      fd.append("install_mode", form.install_mode);
      if (form.install_mode === "scheduled" && form.scheduled_at) {
        fd.append("scheduled_at", new Date(form.scheduled_at).toISOString());
      }
      fd.append("rollout_stage", form.rollout_stage);
      const pct = ROLLOUT_STAGES.find((s) => s.value === form.rollout_stage)?.pct ?? 100;
      fd.append("rollout_percentage", String(pct));
      if (form.failure_threshold_percent) {
        fd.append("failure_threshold_percent", form.failure_threshold_percent);
      }
      const ids = form.deploy_target === "companies" ? companyIds
        : form.deploy_target === "groups" ? groupIds
        : form.deploy_target === "devices" ? deviceIds : [];
      ids.forEach((id) => fd.append("target_ids[]", String(id)));
    }

    try {
      const res: any = await upload(fd).unwrap();
      if (res.status) {
        showMessage(res.message || "Release uploaded");
        reset(); onUploaded(); onClose();
      } else showErrorMessage(res.message || "Upload failed");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Upload failed");
    }
  };

  const labelSx = { fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase" as const, letterSpacing: "0.04em" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Upload Release</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel sx={{ mt: 1, mb: 3 }}>
          {STEPS.map((s) => <Step key={s}><StepLabel><span style={{ fontSize: 11 }}>{s}</span></StepLabel></Step>)}
        </Stepper>

        {/* STEP 0 — Release information */}
        {step === 0 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField select size="small" label="Platform" value={form.platform}
              onChange={(e) => set({ platform: e.target.value })}>
              <MenuItem value="android">Android</MenuItem>
              <MenuItem value="web">Web</MenuItem>
            </TextField>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField size="small" label="Version name" placeholder="1.4.0"
                value={form.version_name} onChange={(e) => set({ version_name: e.target.value })} />
              <TextField size="small" type="number" label="Version code" placeholder="10400"
                value={form.version_code} onChange={(e) => set({ version_code: e.target.value })} />
            </Box>
            <TextField select size="small" label="Release channel" value={form.release_channel}
              onChange={(e) => set({ release_channel: e.target.value })}>
              <MenuItem value="stable">Stable</MenuItem>
              <MenuItem value="beta">Beta</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </TextField>
            <TextField multiline rows={3} size="small" label="Release notes"
              value={form.release_notes} onChange={(e) => set({ release_notes: e.target.value })} />
          </Box>
        )}

        {/* STEP 1 — APK file */}
        {step === 1 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            {form.platform === "web" ? (
              <Alert severity="info">Web releases don't need an APK — the player reloads when the version advances.</Alert>
            ) : (
              <>
                <input type="file" hidden accept=".apk" ref={fileRef}
                  onChange={(e) => onPickFile(e.target.files?.[0] || null)} />
                <Button variant="outlined" startIcon={<CloudUploadIcon />}
                  onClick={() => fileRef.current?.click()}
                  sx={{ textTransform: "none", borderRadius: 2, alignSelf: "start" }}>
                  {file ? "Change APK file" : "Choose APK file"}
                </Button>
                {file && (
                  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
                    <Row label="Filename" value={file.name} />
                    <Row label="File size" value={formatBytes(file.size)} />
                    <Row label="Detected version name" value={detected?.version_name ?? "…"} />
                    <Row label="Detected version code" value={detected?.version_code ?? "…"} />
                    <Row label="Package name" value={detected?.package_name ?? "—"} />
                    <Row label="Minimum Android API" value={detected?.min_sdk_version ?? "—"} />
                    <Row label="SHA-256 checksum" value={detected?.checksum_sha256
                      ? detected.checksum_sha256.slice(0, 24) + "…" : "…"} mono />
                    {detected && detected.version_code != null &&
                      String(detected.version_code) !== form.version_code && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        APK version code ({detected.version_code}) differs from the value you entered ({form.version_code}).
                      </Alert>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* STEP 2 — Deployment target */}
        {step === 2 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography sx={labelSx}>Deploy To</Typography>
              <RadioGroup value={form.deploy_target} onChange={(e) => set({ deploy_target: e.target.value })}>
                {DEPLOY_TARGETS.map((t) => (
                  <FormControlLabel key={t.value} value={t.value}
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>{t.label}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            {form.deploy_target === "companies" && (
              <Autocomplete multiple size="small" options={companies}
                getOptionLabel={(o) => `${o.name} (${o.active_devices} active)`}
                value={companies.filter((c) => companyIds.includes(c.id))}
                onChange={(_e, v) => setCompanyIds(v.map((c) => c.id))}
                renderInput={(p) => <TextField {...p} label="Companies" placeholder="Search companies" />} />
            )}

            {form.deploy_target === "groups" && (
              <Autocomplete multiple size="small" options={groups}
                getOptionLabel={(o) => `${o.name} (${o.device_count} devices)`}
                value={groups.filter((g) => groupIds.includes(g.id))}
                onChange={(_e, v) => setGroupIds(v.map((g) => g.id))}
                renderInput={(p) => <TextField {...p} label="Device groups" placeholder="Search groups" />} />
            )}

            {form.deploy_target === "devices" && (
              <Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <TextField size="small" placeholder="Search" value={dfSearch}
                    onChange={(e) => setDfSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                    sx={{ width: 160 }} />
                  <TextField select size="small" label="Company" value={dfCompany === "" ? "" : String(dfCompany)}
                    onChange={(e) => setDfCompany(e.target.value === "" ? "" : Number(e.target.value))} sx={{ width: 150 }}>
                    <MenuItem value="">All</MenuItem>
                    {companies.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Status" value={dfStatus}
                    onChange={(e) => setDfStatus(e.target.value)} sx={{ width: 120 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </TextField>
                  <TextField select size="small" label="App version" value={dfVersion}
                    onChange={(e) => setDfVersion(e.target.value)} sx={{ width: 130 }}>
                    <MenuItem value="">All</MenuItem>
                    {versionOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Group" value={dfGroup === "" ? "" : String(dfGroup)}
                    onChange={(e) => setDfGroup(e.target.value === "" ? "" : Number(e.target.value))} sx={{ width: 140 }}>
                    <MenuItem value="">All</MenuItem>
                    {groups.map((g) => <MenuItem key={g.id} value={String(g.id)}>{g.name}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ maxHeight: 240, overflow: "auto", border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox size="small"
                            checked={filteredDevices.length > 0 && filteredDevices.every((d) => deviceIds.includes(d.id))}
                            indeterminate={filteredDevices.some((d) => deviceIds.includes(d.id)) &&
                              !filteredDevices.every((d) => deviceIds.includes(d.id))}
                            onChange={(e) => {
                              const ids = filteredDevices.map((d) => d.id);
                              setDeviceIds(e.target.checked
                                ? Array.from(new Set([...deviceIds, ...ids]))
                                : deviceIds.filter((id) => !ids.includes(id)));
                            }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>Code</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>Name</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>Version</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDevices.map((d) => (
                        <TableRow key={d.id} hover onClick={() => setDeviceIds(
                          deviceIds.includes(d.id) ? deviceIds.filter((id) => id !== d.id) : [...deviceIds, d.id]
                        )} sx={{ cursor: "pointer" }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={deviceIds.includes(d.id)} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{d.device_code}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{d.device_name}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{d.app_version || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{d.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
                  {deviceIds.length} selected · {filteredDevices.length} shown
                </Typography>
              </Box>
            )}

            {form.deploy_target !== "none" && form.deploy_target !== "devices" && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <TextField select size="small" label="Staged rollout" value={form.rollout_stage}
                  onChange={(e) => set({ rollout_stage: e.target.value })}>
                  {ROLLOUT_STAGES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Auto-pause at failure %"
                  placeholder="e.g. 10" value={form.failure_threshold_percent}
                  onChange={(e) => set({ failure_threshold_percent: e.target.value })} />
              </Box>
            )}
          </Box>
        )}

        {/* STEP 3 — Update policy */}
        {step === 3 && (
          <Box sx={{ display: "grid", gap: 1 }}>
            {form.deploy_target === "none" ? (
              <Alert severity="info">No deployment selected — policy applies once you deploy this release.</Alert>
            ) : (
              <RadioGroup value={form.policy} onChange={(e) => set({ policy: e.target.value })}>
                {POLICIES.map((p) => (
                  <Box key={p.value} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.5, mb: 1 }}>
                    <FormControlLabel value={p.value} control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: 13, fontWeight: 700 }}>{p.label} update</Typography>} />
                    <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, ml: 4 }}>{p.help}</Typography>
                  </Box>
                ))}
              </RadioGroup>
            )}
          </Box>
        )}

        {/* STEP 4 — Installation schedule */}
        {step === 4 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            {form.deploy_target === "none" ? (
              <Alert severity="info">No deployment selected — scheduling applies once you deploy this release.</Alert>
            ) : (
              <>
                <RadioGroup value={form.install_mode} onChange={(e) => set({ install_mode: e.target.value })}>
                  {INSTALL_MODES.map((m) => (
                    <FormControlLabel key={m.value} value={m.value} control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: 13 }}>{m.label}</Typography>} />
                  ))}
                </RadioGroup>
                {form.install_mode === "scheduled" && (
                  <TextField size="small" type="datetime-local" label="Install at"
                    InputLabelProps={{ shrink: true }} value={form.scheduled_at}
                    onChange={(e) => set({ scheduled_at: e.target.value })} />
                )}
                {form.install_mode === "maintenance_window" && (
                  <Alert severity="info">
                    Installs during each device's maintenance window (e.g. daily 02:00–04:00), configured per company or device.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {/* STEP 5 — Review */}
        {step === 5 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
              <Row label="Version" value={`${form.version_name} (code ${form.version_code})`} />
              <Row label="Platform / channel" value={`${form.platform} · ${form.release_channel}`} />
              {file && <Row label="APK" value={`${file.name} · ${formatBytes(file.size)}`} />}
              <Divider sx={{ my: 1 }} />
              {form.deploy_target === "none" ? (
                <Typography sx={{ fontSize: 13 }}>Uploaded without deploying. You can deploy it later from the release details page.</Typography>
              ) : (
                <>
                  <Row label="Deploy to" value={DEPLOY_TARGETS.find((t) => t.value === form.deploy_target)?.label} />
                  <Row label="Policy" value={<Chip size="small" label={form.policy} sx={{ height: 20, fontSize: 11 }} />} />
                  <Row label="Schedule" value={INSTALL_MODES.find((m) => m.value === form.install_mode)?.label} />
                  <Row label="Staged rollout" value={ROLLOUT_STAGES.find((s) => s.value === form.rollout_stage)?.label} />
                  <Typography sx={{ fontSize: 13, mt: 1, fontWeight: 600, color: NAVY }}>
                    Version {form.version_name} will be uploaded
                    {" "}and deployed to ~{targetCount} device{targetCount === 1 ? "" : "s"}
                    {form.install_mode === "maintenance_window"
                      ? ". Installation occurs during each device's maintenance window." : "."}
                  </Typography>
                </>
              )}
            </Box>
            {needsMandatoryConfirm && (
              <Alert severity="warning">
                <FormControlLabel control={<Checkbox size="small" checked={confirmMandatory}
                  onChange={(e) => setConfirmMandatory(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 13 }}>
                    I confirm deploying this <b>mandatory</b> update to ~{targetCount} device(s).
                  </Typography>} />
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => { reset(); onClose(); }} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && <Button onClick={() => setStep(step - 1)} sx={{ textTransform: "none", fontWeight: 600 }}>Back</Button>}
        {step < STEPS.length - 1 ? (
          <Button variant="contained" disableElevation disabled={!canNext()}
            onClick={() => setStep(step + 1)}
            sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`, color: "#0b1e2d" }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" disableElevation onClick={doUpload}
            disabled={uploading || (needsMandatoryConfirm && !confirmMandatory)}
            sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`, color: "#0b1e2d" }}>
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
    <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY, fontFamily: mono ? "monospace" : undefined, textAlign: "right" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

export default UploadWizard;
